import { Box, IconButton, Stack, Typography } from "@mui/material"
import { hasAmendsContent } from "authoring/routes/project/amends/lib/amends-content"
import { buildContentPreview, hasLongContent } from "authoring/routes/project/amends/lib/amends-content-preview"
import { DATE_FORMATS } from "lib/@constants/date-format"
import { If } from "lib/@components/switch"
import { useDialog } from "lib/@hooks/useDialog"
import Iconify from "minimals-template/components/Iconify"
import { UserAvatar } from "minimals-template/components/user-avatar"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { htmlToText } from "lib/html-to-text"
import { ReturnedToEditingDialog } from "./returned-to-editing-dialog"

export function DraftReturnedToEditingComment({ returnedToEditing }) {
    const openDialog = useDialog(<ReturnedToEditingDialog />, { maxWidth: "sm", fullScreen: false })
    if (!returnedToEditing) return null

    const content = returnedToEditing?.payload?.content
    const dateLabel = returnedToEditing?.date ? new Date(returnedToEditing.date).format(DATE_FORMATS.long) : ""
    const hasContent = hasAmendsContent(content)
    const textContent = hasContent ? htmlToText(content, true).trim() : ""
    const isLongContent = hasContent && hasLongContent(content)
    const previewContent = hasContent ? buildContentPreview(content) : ""
    const displayContent = isLongContent ? previewContent : textContent

    return (
        <Box sx={{ my: 2, display: "flex", minWidth: "100%" }} data-cy="authoring-draft-returned-to-editing-banner">
            <Box sx={{ m: 1, mr: 2 }}>
                <UserAvatar sx={{ width: 32, height: 32 }} user={{ displayName: returnedToEditing?.userName }} />
            </Box>
            <Box sx={{ minWidth: "80%" }}>
                <Box display="flex" alignItems="center" sx={{ my: 1 }}>
                    <TruncatedTypography
                        variant="caption"
                        sx={{ color: "text.secondary", maxWidth: "fit-content", flex: 1 }}
                    >
                        {returnedToEditing?.userName}
                    </TruncatedTypography>
                    <Typography variant={"caption"} sx={{ color: "text.secondary", flexShrink: 0 }}>
                        , {dateLabel}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        bgcolor: "background.neutral",
                        borderRadius: 2,
                        p: 1.5,
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ mb: hasContent ? 0.5 : 0 }}>
                                Returned to Editing
                            </Typography>
                            <If truthy={hasContent}>
                                <Typography
                                    data-cy="returned-to-editing-comment-preview"
                                    variant="body2"
                                    sx={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {displayContent}
                                </Typography>
                            </If>
                        </Box>
                        <If truthy={hasContent}>
                            <IconButton
                                aria-label="View comment"
                                size="small"
                                color="primary"
                                sx={{ p: 0.25, mt: 0.25 }}
                                onClick={showDialog}
                                data-cy="returned-to-editing-comment-view-details"
                            >
                                <Iconify icon="mdi:eye" width={18} />
                            </IconButton>
                        </If>
                    </Stack>
                </Box>
            </Box>
        </Box>
    )

    async function showDialog() {
        await openDialog({ content })
    }
}
